/**
 * Render IRGraph as a draw.io State Diagram.
 * State boxes with rounded corners, filled start/end circles, transitions as edges.
 * Supports composite state containers (rendered as rounded container cells).
 */

import type { LayoutResult, IRNode, IRGraph, ColorPalette } from '../types';
import { NodeShape } from '../types';
import { SHAPE_REGISTRY, EDGE_STYLE, EDGE_STYLE_DASHED } from '../registry';
import { getShapeColors, applyColors } from '../color';
import { esc, IdGen, wrapMxfile, vertexCell, edgeCell, labelCell } from './xml-builder';

/** Height of the swimlane header bar for composite containers */
const COMPOSITE_HEADER_SIZE = 20;

/** Get absolute bounding box for a node with optional margin */
function getNodeAbsBox(
  node: IRNode,
  graph: IRGraph,
  margin = 0,
): { left: number; right: number; top: number; bottom: number } {
  const lane = node.laneId ? graph.lanes.find((l) => l.id === node.laneId) : null;
  const lx = lane?.x ?? 0;
  const ly = lane?.y ?? 0;
  const ax = lx + (node.x ?? 0);
  const ay = ly + (node.y ?? 0);
  return {
    left: ax - margin,
    right: ax + node.width + margin,
    top: ay - margin,
    bottom: ay + node.height + margin,
  };
}

/** Check if a line segment intersects an AABB */
function segmentHitsBox(
  x1: number, y1: number, x2: number, y2: number,
  box: { left: number; right: number; top: number; bottom: number },
): boolean {
  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  if (maxX < box.left || minX > box.right) return false;
  if (maxY < box.top || minY > box.bottom) return false;

  // Horizontal segment
  if (Math.abs(y1 - y2) < 1) {
    return y1 >= box.top && y1 <= box.bottom && maxX >= box.left && minX <= box.right;
  }
  // Vertical segment
  if (Math.abs(x1 - x2) < 1) {
    return x1 >= box.left && x1 <= box.right && maxY >= box.top && minY <= box.bottom;
  }

  return true;
}

/** Check if the straight path between two centers hits any intermediate node */
function isPathObstructed(
  srcC: { x: number; y: number },
  tgtC: { x: number; y: number },
  srcId: string,
  tgtId: string,
  graph: IRGraph,
): boolean {
  for (const [nid, node] of graph.nodes) {
    if (nid === srcId || nid === tgtId) continue;
    const box = getNodeAbsBox(node, graph, 4);
    if (segmentHitsBox(srcC.x, srcC.y, tgtC.x, tgtC.y, box)) {
      return true;
    }
  }
  return false;
}

/** Pick the less-crowded side perpendicular to the primary src→tgt direction */
function pickLessCrowdedSide(
  srcC: { x: number; y: number },
  tgtC: { x: number; y: number },
  srcId: string,
  tgtId: string,
  graph: IRGraph,
): 'sideA' | 'sideB' {
  const dx = tgtC.x - srcC.x;
  const dy = tgtC.y - srcC.y;
  const primaryVertical = Math.abs(dy) >= Math.abs(dx);

  let countA = 0;
  let countB = 0;
  const midPrimary = primaryVertical
    ? (srcC.y + tgtC.y) / 2
    : (srcC.x + tgtC.x) / 2;
  const midPerp = primaryVertical
    ? (srcC.x + tgtC.x) / 2
    : (srcC.y + tgtC.y) / 2;

  for (const [nid, node] of graph.nodes) {
    if (nid === srcId || nid === tgtId) continue;
    const box = getNodeAbsBox(node, graph);
    const nodeCenter = primaryVertical
      ? (box.left + box.right) / 2
      : (box.top + box.bottom) / 2;
    // Only count nodes roughly in the corridor between src and tgt
    const nodePrimary = primaryVertical
      ? (box.top + box.bottom) / 2
      : (box.left + box.right) / 2;
    const minP = Math.min(primaryVertical ? srcC.y : srcC.x, primaryVertical ? tgtC.y : tgtC.x);
    const maxP = Math.max(primaryVertical ? srcC.y : srcC.x, primaryVertical ? tgtC.y : tgtC.x);
    if (nodePrimary < minP || nodePrimary > maxP) continue;

    if (nodeCenter < midPerp) countA++;
    else countB++;
  }

  return countA <= countB ? 'sideA' : 'sideB';
}

export function renderDrawioState(
  layout: LayoutResult,
  palette: ColorPalette,
): string {
  const gen = new IdGen();
  const graph = layout.graph;
  const cells: string[] = [];

  const nodeCellMap = new Map<string, string>();
  const laneCellMap = new Map<string, string>();

  // Identify composite lanes (non-_general lanes)
  const compositeLanes = graph.lanes.filter((l) => l.id !== '_general');

  // Render composite state containers
  for (const lane of compositeLanes) {
    const cellId = gen.next();
    laneCellMap.set(lane.id, cellId);

    // Nested composites: parent to outer container; top-level: parent to '1'
    const parentCell = lane.parentLaneId && laneCellMap.has(lane.parentLaneId)
      ? laneCellMap.get(lane.parentLaneId)!
      : '1';

    const containerStyle =
      'swimlane;startSize=20;rounded=1;shadow=0;glass=0;swimlaneLine=1;';

    const x = parentCell === '1' ? layout.containerX + lane.x : lane.x;
    const y = parentCell === '1' ? layout.containerY + lane.y : lane.y;
    const h = lane.height + COMPOSITE_HEADER_SIZE;

    cells.push(
      `        <mxCell id="${cellId}" value="${esc(lane.title)}" ` +
      `style="${containerStyle}" vertex="1" parent="${parentCell}">\n` +
      `          <mxGeometry x="${x}" y="${y}" ` +
      `width="${lane.width}" height="${h}" as="geometry">\n` +
      `            <mxRectangle x="${x}" y="${y}" width="90" height="30" as="alternateBounds" />\n` +
      `          </mxGeometry>\n` +
      `        </mxCell>`,
    );
  }

  // Render nodes
  for (const [nid, node] of graph.nodes) {
    if (node.x === undefined || node.y === undefined) continue;

    const cellId = gen.next();
    nodeCellMap.set(nid, cellId);

    let style = getStateStyle(node);
    const colors = getShapeColors(node.shape, palette);
    style = applyColors(style, colors);

    const value = isBlankShape(node.shape) ? '' : node.label;

    // Determine parent cell and coordinates
    let parentCell = '1';
    let nx = layout.containerX + (node.x ?? 0);
    let ny = layout.containerY + (node.y ?? 0);

    if (node.laneId && node.laneId !== '_general' && laneCellMap.has(node.laneId)) {
      // Node inside a composite container — coords are lane-relative, offset by header
      parentCell = laneCellMap.get(node.laneId)!;
      nx = node.x ?? 0;
      ny = (node.y ?? 0) + COMPOSITE_HEADER_SIZE;
    } else {
      // Node in _general — absolute coords with lane offset
      const lane = node.laneId ? graph.lanes.find((l) => l.id === node.laneId) : null;
      if (lane) {
        nx = layout.containerX + lane.x + (node.x ?? 0);
        ny = layout.containerY + lane.y + (node.y ?? 0);
      }
    }

    cells.push(
      vertexCell(cellId, value, style, parentCell, nx, ny, node.width, node.height),
    );
  }

  // Render edges
  for (const edge of graph.edges) {
    const srcCell = nodeCellMap.get(edge.source);
    const tgtCell = nodeCellMap.get(edge.target);
    if (!srcCell || !tgtCell) continue;

    // Determine if this edge crosses a composite container boundary
    const srcNode = graph.nodes.get(edge.source);
    const tgtNode = graph.nodes.get(edge.target);
    const srcInComposite = !!srcNode?.laneId && srcNode.laneId !== '_general'
      && laneCellMap.has(srcNode.laneId);
    const tgtInComposite = !!tgtNode?.laneId && tgtNode.laneId !== '_general'
      && laneCellMap.has(tgtNode.laneId);
    const isCrossContainer =
      (srcInComposite && !tgtInComposite) ||
      (!srcInComposite && tgtInComposite) ||
      (srcInComposite && tgtInComposite && srcNode!.laneId !== tgtNode!.laneId);

    // Determine edge parent — cross-container → root; intra-composite → composite cell
    let edgeParent: string;
    if (isCrossContainer || edge.crossLane) {
      edgeParent = '1';
    } else {
      edgeParent = srcNode?.laneId && laneCellMap.has(srcNode.laneId)
        ? laneCellMap.get(srcNode.laneId)!
        : '1';
    }

    let style = edge.dashed ? EDGE_STYLE_DASHED : EDGE_STYLE;

    // Apply exit points
    const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
    if (exitPt) {
      style += `exitX=${exitPt.x};exitY=${exitPt.y};exitDx=0;exitDy=0;`;
    }

    // Apply entry points
    const entryPt = edge.meta?.entryPoint as { x: number; y: number } | undefined;
    if (entryPt) {
      style += `entryX=${entryPt.x};entryY=${entryPt.y};entryDx=0;entryDy=0;`;
    }

    // For cross-container edges without explicit exit/entry points,
    // compute obstruction-aware exit/entry
    if (isCrossContainer && srcNode && tgtNode) {
      const srcC = getNodeAbsCenter(srcNode, graph);
      const tgtC = getNodeAbsCenter(tgtNode, graph);

      const dx = tgtC.x - srcC.x;
      const dy = tgtC.y - srcC.y;

      // Default exit/entry based on primary direction
      let defaultExit: { x: number; y: number };
      let defaultEntry: { x: number; y: number };
      if (Math.abs(dy) >= Math.abs(dx)) {
        defaultExit = dy >= 0 ? { x: 0.5, y: 1 } : { x: 0.5, y: 0 };
        defaultEntry = dy >= 0 ? { x: 0.5, y: 0 } : { x: 0.5, y: 1 };
      } else {
        defaultExit = dx >= 0 ? { x: 1, y: 0.5 } : { x: 0, y: 0.5 };
        defaultEntry = dx >= 0 ? { x: 0, y: 0.5 } : { x: 1, y: 0.5 };
      }

      // Check if straight path srcC→tgtC hits any intermediate node
      const obstructed = isPathObstructed(srcC, tgtC, edge.source, edge.target, graph);

      if (obstructed) {
        // Pick side exit/entry perpendicular to the primary direction
        const side = pickLessCrowdedSide(srcC, tgtC, edge.source, edge.target, graph);
        if (Math.abs(dy) >= Math.abs(dx)) {
          // Primary is vertical → use left or right side
          const sidePort = side === 'sideA'
            ? { x: 0, y: 0.5 }   // left
            : { x: 1, y: 0.5 };  // right
          defaultExit = sidePort;
          defaultEntry = sidePort;
        } else {
          // Primary is horizontal → use top or bottom side
          const sidePort = side === 'sideA'
            ? { x: 0.5, y: 0 }   // top
            : { x: 0.5, y: 1 };  // bottom
          defaultExit = sidePort;
          defaultEntry = sidePort;
        }
      }

      if (!exitPt) {
        style += `exitX=${defaultExit.x};exitY=${defaultExit.y};exitDx=0;exitDy=0;`;
      }
      if (!entryPt) {
        style += `entryX=${defaultEntry.x};entryY=${defaultEntry.y};entryDx=0;entryDy=0;`;
      }
    }

    const edgeId = gen.next();
    // Strip routePoints for cross-container edges — let draw.io auto-router handle them
    const routePoints = isCrossContainer
      ? undefined
      : edge.meta?.routePoints as Array<{ x: number; y: number }> | undefined;
    const adjusted = routePoints ? adjustRoutePoints(routePoints, edge, isCrossContainer, graph, layout) : undefined;
    cells.push(edgeCell(edgeId, '', style, edgeParent, srcCell, tgtCell, adjusted));

    if (edge.label) {
      const lblId = gen.next();
      cells.push(labelCell(lblId, edge.label, edgeId));
    }
  }

  return wrapMxfile(cells);
}

function getStateStyle(node: IRNode): string {
  switch (node.shape) {
    case NodeShape.StartCircle:
      return 'ellipse;fillColor=#000000;strokeColor=#000000;html=1;';
    case NodeShape.EndCircle:
      return 'ellipse;html=1;shape=endState;fillColor=#000000;strokeColor=#000000;';
    case NodeShape.Decision:
      return SHAPE_REGISTRY[NodeShape.Decision].activityStyle ??
        'shape=mxgraph.flowchart.decision;whiteSpace=wrap;html=1;';
    case NodeShape.ForkBar:
    case NodeShape.JoinBar:
      return 'html=1;points=[];perimeter=orthogonalPerimeter;fillColor=#000000;strokeColor=#000000;';
    default:
      return 'rounded=1;whiteSpace=wrap;html=1;arcSize=20;';
  }
}

/**
 * Compute the absolute center of a node, accounting for lane offsets.
 */
function getNodeAbsCenter(
  node: IRNode,
  graph: IRGraph,
): { x: number; y: number } {
  const lane = node.laneId ? graph.lanes.find((l) => l.id === node.laneId) : null;
  const lx = lane?.x ?? 0;
  const ly = lane?.y ?? 0;
  return {
    x: lx + (node.x ?? 0) + node.width / 2,
    y: ly + (node.y ?? 0) + node.height / 2,
  };
}

function isBlankShape(shape: NodeShape): boolean {
  return [
    NodeShape.StartCircle,
    NodeShape.EndCircle,
    NodeShape.ForkBar,
    NodeShape.JoinBar,
  ].includes(shape);
}

/**
 * Convert route points from edge-rule space to rendering space.
 * Mirrors drawio-flowchart.ts adjustRoutePoints logic.
 */
function adjustRoutePoints(
  routePoints: Array<{ x: number; y: number }>,
  edge: { crossLane?: boolean; source: string },
  isCrossContainer: boolean,
  graph: IRGraph,
  layout: LayoutResult,
): Array<{ x: number; y: number }> {
  const srcNode = graph.nodes.get(edge.source);
  const isIntraComposite =
    !isCrossContainer && !edge.crossLane && !!srcNode?.laneId && srcNode.laneId !== '_general';

  if (isIntraComposite) {
    // Edge parented to composite — route points already lane-relative, no offset needed
    return routePoints;
  }

  // Cross-container or _general: add container offset
  let offX = layout.containerX;
  let offY = layout.containerY;

  if (!isCrossContainer && !edge.crossLane && srcNode?.laneId) {
    const lane = graph.lanes.find((l) => l.id === srcNode.laneId);
    offX += lane?.x ?? 0;
    offY += lane?.y ?? 0;
  }

  return routePoints.map((p) => ({ x: p.x + offX, y: p.y + offY }));
}
